import { useCallback, useSyncExternalStore } from "react";

/**
 * A hash router, in about forty lines.
 *
 * Hash routing rather than the History API because the portal is a static
 * bundle behind an API that serves no HTML: with `/jobs/abc` a reload asks the
 * web server for a file that does not exist, and the fix is a rewrite rule in
 * whatever serves it — one more thing to get right in every environment. The
 * hash never reaches the server.
 *
 * No router library because the whole route table is four entries and the
 * requirement is that links are real `<a href>` elements a browser can open in
 * a new tab, which is a property of the markup, not of a dependency.
 */

export type Route =
  | { readonly name: "pipeline" }
  // Every survey, newest first — the survey-only portal's one list and its
  // home. `pipeline` is kept: it is still the right board for a company
  // running installs, and nothing is gained by breaking its link.
  | { readonly name: "surveys" }
  | { readonly name: "deadlines" }
  // Every job of the organisation on a UK map (migration 0041's postcode cache).
  | { readonly name: "jobs-map" }
  // Jobs being worked on in the field: an installer on them, or photographs
  // off them. A view over jobs rather than a record of its own.
  | { readonly name: "installations" }
  // One installation's photographs. Deliberately its OWN route rather than a
  // link into the job's §6.11 evidence screen: installations and surveys are
  // separate sections, and a row that jumped between them put you in a
  // different part of the portal with a different sidebar item lit.
  | { readonly name: "installation"; readonly jobId: string }
  | { readonly name: "contacts" }
  | { readonly name: "tasks" }
  | { readonly name: "new-lead" }
  | { readonly name: "job"; readonly id: string }
  // Where a survey opens: what it is, what it still needs, and the ways into
  // it. See the note in `parseRoute` for why this took the bare address.
  | { readonly name: "survey-overview"; readonly id: string }
  // The dense working screen — the plan, the walls, the exports.
  | { readonly name: "survey"; readonly id: string }
  // The survey's rooms, grouped by the floor each was walked on.
  | { readonly name: "survey-rooms"; readonly id: string }
  // One room on its own: its walls, what each faces, and what it still owes.
  | { readonly name: "survey-room"; readonly id: string; readonly roomId: string }
  // The readiness checks, conditions, materials, heat loss and assumptions —
  // the screen's full form, which "the drawing" was carved out of. It keeps a
  // door of its own so nothing is orphaned by that carving.
  | { readonly name: "survey-checks"; readonly id: string }
  | { readonly name: "handoff" }
  // Agreements a customer has signed and the company has not. One screen for
  // the whole organisation, so not under a job.
  | { readonly name: "countersign" }
  // The survey's stock report and installer pack (migration 0033).
  | { readonly name: "survey-stock"; readonly id: string }
  // One section of the survey's site survey (migration 0056).
  | { readonly name: "survey-site"; readonly id: string; readonly section: string }
  | { readonly name: "job-documents"; readonly jobId: string }
  // What was agreed, which a contract is produced from (migration 0023).
  | { readonly name: "job-contract"; readonly jobId: string }
  | { readonly name: "document-finder" }
  // PAS 2035 is a second compliance regime over its own records, so it gets its
  // own two routes rather than being folded into the job ones.
  | { readonly name: "retrofits" }
  | { readonly name: "retrofit"; readonly id: string }
  // Chat and the notification hub are the two screens somebody keeps open all
  // day, so both are top-level rather than hanging off a job. A conversation
  // ABOUT a job is still reached from the job as well.
  | { readonly name: "chat" }
  | { readonly name: "chat-thread"; readonly id: string }
  | { readonly name: "notifications" }
  // The owner/admin team management screen: who works here, what role, active.
  | { readonly name: "team" }
  // The company itself: the name, number and address that go on a contract,
  // and who countersigns one. Set up once, by an owner or admin.
  | { readonly name: "company" }
  | { readonly name: "recycle-bin" }
  // The §6.11 photographic record for one job.
  | { readonly name: "job-photos"; readonly jobId: string }
  // The Boiler Upgrade Scheme grant screen for one job — the verdict input
  // form, the resulting eligibility, and the two BUS clocks.
  | { readonly name: "bus-grant"; readonly jobId: string }
  // Manual heat-pump entry with checkSizing feedback for one job.
  | { readonly name: "heat-pump"; readonly jobId: string }
  // Room-by-room emitter selection with assessEmitters feedback.
  | { readonly name: "emitters"; readonly jobId: string }
  // The two routes reached from an email, by somebody with no session — and,
  // for an invitation, no account at all. The API composes both URLs
  // (`invitationUrl` / `passwordResetUrl` in
  // `apps/api/src/auth/account-mail.ts`), so these two shapes are a contract
  // with that file rather than a choice this router is free to make: get one
  // wrong and every invitation email lands on "Not a page".
  | { readonly name: "invitation"; readonly token: string }
  | { readonly name: "reset-password"; readonly token: string }
  // The Help centre: a searchable list of short guides, and one guide each.
  | { readonly name: "help" }
  | { readonly name: "help-guide"; readonly id: string }
  | { readonly name: "unknown"; readonly path: string };

export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#/, "").replace(/^\/+/, "").split("?")[0] ?? "";
  const segments = path.split("/").filter(Boolean).map(decodeURIComponent);

  // The survey list is home. A surveyor opens this portal to deal with what
  // has just arrived off the phone, and that is the only list there is.
  if (segments.length === 0) return { name: "surveys" };
  if (segments.length === 1 && segments[0] === "surveys") return { name: "surveys" };
  if (segments.length === 1 && segments[0] === "pipeline") return { name: "pipeline" };
  if (segments.length === 1 && segments[0] === "deadlines") {
    return { name: "deadlines" };
  }
  if (segments.length === 1 && segments[0] === "contacts") {
    return { name: "contacts" };
  }
  if (segments.length === 1 && segments[0] === "map") {
    return { name: "jobs-map" };
  }
  // The standing connection to portal.scannix.co. Not per survey, so not
  // under /surveys: it is set up once for the whole organisation.
  if (segments.length === 1 && segments[0] === "handoff") {
    return { name: "handoff" };
  }
  if (segments.length === 1 && segments[0] === "countersign") {
    return { name: "countersign" };
  }
  if (segments.length === 1 && segments[0] === "installations") {
    return { name: "installations" };
  }
  if (segments.length === 2 && segments[0] === "installations" && segments[1]) {
    return { name: "installation", jobId: segments[1] };
  }
  if (segments.length === 1 && segments[0] === "tasks") {
    return { name: "tasks" };
  }
  // Two segments and neither is an id: taking a lead is an act, not a record,
  // so it lives under the collection it creates into rather than at a bare
  // "/new" that would collide with the next thing anybody wants to create.
  if (segments.length === 2 && segments[0] === "leads" && segments[1] === "new") {
    return { name: "new-lead" };
  }
  if (segments.length === 2 && segments[0] === "jobs" && segments[1]) {
    return { name: "job", id: segments[1] };
  }
  /*
   * A SURVEY OPENS ON ITS OVERVIEW, not on the plan editor.
   *
   * `#/surveys/<id>` used to be the dense working screen: the plan, the rooms,
   * the walls, the readiness checks and the exports all at once. That is the
   * right screen for an hour of design work and the wrong one to ARRIVE at, so
   * the drawing moved to `/plan` and this address now answers the two
   * questions somebody opening a survey actually has — what is this, and what
   * does it still need.
   *
   * Every existing link to `href.survey(id)` therefore lands on the overview,
   * which is the intent: the surveys list, the job screen's list of surveys,
   * and anything already bookmarked.
   */
  if (segments.length === 2 && segments[0] === "surveys" && segments[1]) {
    return { name: "survey-overview", id: segments[1] };
  }
  if (segments.length === 3 && segments[0] === "surveys" && segments[1] && segments[2] === "plan") {
    return { name: "survey", id: segments[1] };
  }
  if (segments.length === 3 && segments[0] === "surveys" && segments[1] && segments[2] === "rooms") {
    return { name: "survey-rooms", id: segments[1] };
  }
  if (segments.length === 3 && segments[0] === "surveys" && segments[1] && segments[2] === "checks") {
    return { name: "survey-checks", id: segments[1] };
  }
  // One room on its own. Four segments, so it cannot collide with the three
  // above however a room is named.
  if (
    segments.length === 4 &&
    segments[0] === "surveys" &&
    segments[1] &&
    segments[2] === "rooms" &&
    segments[3]
  ) {
    return { name: "survey-room", id: segments[1], roomId: segments[3] };
  }
  if (
    segments.length === 3 &&
    segments[0] === "surveys" &&
    segments[1] &&
    segments[2] === "stock"
  ) {
    return { name: "survey-stock", id: segments[1] };
  }
  if (segments.length === 4 && segments[0] === "surveys" && segments[1] && segments[2] === "site" && segments[3]) {
    return { name: "survey-site", id: segments[1], section: segments[3] };
  }
  if (segments.length === 1 && segments[0] === "documents") {
    return { name: "document-finder" };
  }
  if (
    segments.length === 3 &&
    segments[0] === "jobs" &&
    segments[1] &&
    segments[2] === "documents"
  ) {
    return { name: "job-documents", jobId: segments[1] };
  }
  if (
    segments.length === 3 &&
    segments[0] === "jobs" &&
    segments[1] &&
    segments[2] === "contract"
  ) {
    return { name: "job-contract", jobId: segments[1] };
  }
  if (segments.length === 1 && segments[0] === "chat") {
    return { name: "chat" };
  }
  if (segments.length === 2 && segments[0] === "chat" && segments[1]) {
    return { name: "chat-thread", id: segments[1] };
  }
  if (segments.length === 1 && segments[0] === "notifications") {
    return { name: "notifications" };
  }
  if (segments.length === 1 && segments[0] === "recycle-bin") {
    return { name: "recycle-bin" };
  }
  if (segments.length === 1 && segments[0] === "company") {
    return { name: "company" };
  }
  if (segments.length === 1 && segments[0] === "team") {
    return { name: "team" };
  }
  if (
    segments.length === 3 &&
    segments[0] === "jobs" &&
    segments[1] &&
    segments[2] === "photos"
  ) {
    return { name: "job-photos", jobId: segments[1] };
  }
  if (
    segments.length === 3 &&
    segments[0] === "jobs" &&
    segments[1] &&
    segments[2] === "bus-grant"
  ) {
    return { name: "bus-grant", jobId: segments[1] };
  }
  if (
    segments.length === 3 &&
    segments[0] === "jobs" &&
    segments[1] &&
    segments[2] === "heat-pump"
  ) {
    return { name: "heat-pump", jobId: segments[1] };
  }
  if (
    segments.length === 3 &&
    segments[0] === "jobs" &&
    segments[1] &&
    segments[2] === "emitters"
  ) {
    return { name: "emitters", jobId: segments[1] };
  }
  if (segments.length === 1 && segments[0] === "retrofit") {
    return { name: "retrofits" };
  }
  if (segments.length === 2 && segments[0] === "retrofit" && segments[1]) {
    return { name: "retrofit", id: segments[1] };
  }
  if (segments.length === 2 && segments[0] === "invitation" && segments[1]) {
    return { name: "invitation", token: segments[1] };
  }
  if (segments.length === 2 && segments[0] === "reset-password" && segments[1]) {
    return { name: "reset-password", token: segments[1] };
  }
  if (segments.length === 1 && segments[0] === "help") {
    return { name: "help" };
  }
  if (segments.length === 2 && segments[0] === "help" && segments[1]) {
    return { name: "help-guide", id: segments[1] };
  }
  return { name: "unknown", path: `/${path}` };
}

export const href = {
  surveys: () => "#/",
  pipeline: () => "#/pipeline",
  deadlines: () => "#/deadlines",
  jobsMap: () => "#/map",
  installations: () => "#/installations",
  installation: (jobId: string) => `#/installations/${encodeURIComponent(jobId)}`,
  contacts: () => "#/contacts",
  tasks: () => "#/tasks",
  newLead: () => "#/leads/new",
  job: (id: string) => `#/jobs/${encodeURIComponent(id)}`,
  survey: (id: string) => `#/surveys/${encodeURIComponent(id)}`,
  surveyPlan: (id: string) => `#/surveys/${encodeURIComponent(id)}/plan`,
  surveyRooms: (id: string) => `#/surveys/${encodeURIComponent(id)}/rooms`,
  surveyChecks: (id: string) => `#/surveys/${encodeURIComponent(id)}/checks`,
  handoff: () => "#/handoff",
  countersign: () => "#/countersign",
  surveyRoom: (id: string, roomId: string) =>
    `#/surveys/${encodeURIComponent(id)}/rooms/${encodeURIComponent(roomId)}`,
  surveyStock: (id: string) => `#/surveys/${encodeURIComponent(id)}/stock`,
  surveySite: (id: string, section: string) =>
    `#/surveys/${encodeURIComponent(id)}/site/${encodeURIComponent(section)}`,
  jobDocuments: (id: string) => `#/jobs/${encodeURIComponent(id)}/documents`,
  jobContract: (id: string) => `#/jobs/${encodeURIComponent(id)}/contract`,
  documentFinder: () => "#/documents",
  retrofits: () => "#/retrofit",
  retrofit: (id: string) => `#/retrofit/${encodeURIComponent(id)}`,
  chat: () => "#/chat",
  chatThread: (id: string) => `#/chat/${encodeURIComponent(id)}`,
  notifications: () => "#/notifications",
  team: () => "#/team",
  company: () => "#/company",
  recycleBin: () => "#/recycle-bin",
  jobPhotos: (id: string) => `#/jobs/${encodeURIComponent(id)}/photos`,
  busGrant: (id: string) => `#/jobs/${encodeURIComponent(id)}/bus-grant`,
  heatPump: (id: string) => `#/jobs/${encodeURIComponent(id)}/heat-pump`,
  emitters: (id: string) => `#/jobs/${encodeURIComponent(id)}/emitters`,
  help: () => "#/help",
  helpGuide: (id: string) => `#/help/${encodeURIComponent(id)}`,
  // Nothing in the portal links to these two — the API puts them in an email.
  // They are here so the portal's spelling of each link lives beside every
  // other route rather than as a string literal in one screen, and so a test
  // can check this spelling against the URL `account-mail.ts` actually sends.
  invitation: (token: string) => `#/invitation/${encodeURIComponent(token)}`,
  resetPassword: (token: string) =>
    `#/reset-password/${encodeURIComponent(token)}`,
};

function subscribe(listener: () => void): () => void {
  globalThis.addEventListener("hashchange", listener);
  return () => {
    globalThis.removeEventListener("hashchange", listener);
  };
}

function currentHash(): string {
  return globalThis.location?.hash ?? "";
}

export function useRoute(): Route {
  // The raw hash is the snapshot rather than the parsed route: parsing on every
  // call would return a fresh object and re-render without end.
  const hash = useSyncExternalStore(subscribe, currentHash, () => "");
  return parseRoute(hash);
}

export function useNavigate(): (to: string) => void {
  return useCallback((to: string) => {
    globalThis.location.hash = to.startsWith("#") ? to.slice(1) : to;
  }, []);
}
